import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI, paymentAPI, libraryAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { 
    Loader2, 
    User, 
    Mail, 
    Calendar, 
    Lock, 
    Edit2, 
    Save, 
    X, 
    ShoppingBag,
    Video,
    CheckCircle,
    Clock,
    AlertCircle,
    LogOut,
    ShieldCheck,
    Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatPrice } from '../lib/utils';

export const ProfilePage = () => {
    const { user, isAuthenticated, loading: authLoading, refreshUser, logoutAllDevices } = useAuth();
    const [loading, setLoading] = useState(true);
    const [purchases, setPurchases] = useState([]);
    const [library, setLibrary] = useState(null);
    const [authStatus, setAuthStatus] = useState(null);
    
    // Edit profile state
    const [editingProfile, setEditingProfile] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);
    
    // Password state (set new or change existing)
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [savingPassword, setSavingPassword] = useState(false);
    
    // Logout all devices state
    const [loggingOutAll, setLoggingOutAll] = useState(false);

    useEffect(() => {
        if (isAuthenticated && user) {
            fetchData();
            setFirstName(user.first_name || '');
            setLastName(user.last_name || '');
        }
    }, [isAuthenticated, user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [purchasesRes, libraryRes, authStatusRes] = await Promise.all([
                paymentAPI.getMyPurchases(),
                libraryAPI.getMyLibrary(),
                authAPI.getAuthStatus()
            ]);
            setPurchases(purchasesRes.data.transactions || []);
            setLibrary(libraryRes.data);
            setAuthStatus(authStatusRes.data);
        } catch (err) {
            console.error('Failed to fetch profile data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProfile = async () => {
        setSavingProfile(true);
        try {
            await authAPI.updateProfile(firstName, lastName);
            await refreshUser();
            setEditingProfile(false);
            toast.success('Profile updated successfully');
        } catch (err) {
            console.error('Failed to update profile:', err);
            toast.error(err.response?.data?.detail || 'Failed to update profile');
        } finally {
            setSavingProfile(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }
        
        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        
        setSavingPassword(true);
        try {
            // If user has no password, use set-password endpoint
            // Otherwise, use change-password endpoint
            if (authStatus?.can_set_password) {
                await authAPI.setPassword(newPassword);
                // Update auth status to reflect new password
                setAuthStatus(prev => ({ ...prev, has_password: true, can_set_password: false }));
                toast.success('Password created successfully! You can now login with email and password.');
            } else {
                await authAPI.changePassword(currentPassword, newPassword);
                toast.success('Password changed successfully');
            }
            setShowPasswordForm(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            console.error('Failed to update password:', err);
            toast.error(err.response?.data?.detail || 'Failed to update password');
        } finally {
            setSavingPassword(false);
        }
    };

    const cancelEditProfile = () => {
        setEditingProfile(false);
        setFirstName(user?.first_name || '');
        setLastName(user?.last_name || '');
    };

    // Wait for auth check
    if (authLoading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    // Redirect if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    const totalVideosOwned = library ? 
        (library.workshops?.length || 0) + 
        (library.sculptures?.length || 0) + 
        (library.courses?.reduce((acc, c) => acc + (c.lessons?.length || 0), 0) || 0) 
        : 0;

    return (
        <div className="section-gap" data-testid="profile-page">
            <div className="container-main max-w-4xl">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="font-serif text-4xl font-semibold text-primary">
                        My Account
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Manage your profile and view your purchase history
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Profile Information */}
                        <Card data-testid="profile-info-card">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <User className="h-5 w-5" />
                                            Profile Information
                                        </CardTitle>
                                        <CardDescription>
                                            Your personal details
                                        </CardDescription>
                                    </div>
                                    {!editingProfile && (
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => setEditingProfile(true)}
                                            data-testid="edit-profile-btn"
                                        >
                                            <Edit2 className="h-4 w-4 mr-1" />
                                            Edit
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {editingProfile ? (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="firstName">First Name</Label>
                                                <Input
                                                    id="firstName"
                                                    value={firstName}
                                                    onChange={(e) => setFirstName(e.target.value)}
                                                    placeholder="Enter first name"
                                                    data-testid="first-name-input"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="lastName">Last Name</Label>
                                                <Input
                                                    id="lastName"
                                                    value={lastName}
                                                    onChange={(e) => setLastName(e.target.value)}
                                                    placeholder="Enter last name"
                                                    data-testid="last-name-input"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button 
                                                onClick={handleSaveProfile}
                                                disabled={savingProfile}
                                                className="bg-accent text-accent-foreground hover:bg-accent/90"
                                                data-testid="save-profile-btn"
                                            >
                                                {savingProfile ? (
                                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                ) : (
                                                    <Save className="h-4 w-4 mr-2" />
                                                )}
                                                Save Changes
                                            </Button>
                                            <Button 
                                                variant="outline"
                                                onClick={cancelEditProfile}
                                                disabled={savingProfile}
                                            >
                                                <X className="h-4 w-4 mr-2" />
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <Mail className="h-4 w-4 text-muted-foreground" />
                                            <div>
                                                <p className="text-sm text-muted-foreground">Email</p>
                                                <p className="font-medium" data-testid="profile-email">{user?.email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <User className="h-4 w-4 text-muted-foreground" />
                                            <div>
                                                <p className="text-sm text-muted-foreground">Name</p>
                                                <p className="font-medium" data-testid="profile-name">
                                                    {user?.first_name || user?.last_name 
                                                        ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
                                                        : <span className="text-muted-foreground italic">Not set</span>
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Calendar className="h-4 w-4 text-muted-foreground" />
                                            <div>
                                                <p className="text-sm text-muted-foreground">Member Since</p>
                                                <p className="font-medium" data-testid="profile-joined">
                                                    {user?.created_at ? formatDate(user.created_at) : 'Unknown'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Password Section */}
                        <Card data-testid="password-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Lock className="h-5 w-5" />
                                    {authStatus?.has_password ? 'Password' : 'Add Password Login'}
                                </CardTitle>
                                <CardDescription>
                                    {authStatus?.has_password 
                                        ? 'Change your account password'
                                        : 'Set up password login as an alternative to magic links or social login'
                                    }
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {showPasswordForm ? (
                                    <form 
                                        onSubmit={handleChangePassword} 
                                        className="space-y-4"
                                        aria-labelledby="password-form-title"
                                    >
                                        <h4 id="password-form-title" className="sr-only">
                                            {authStatus?.has_password ? 'Change Password Form' : 'Create Password Form'}
                                        </h4>
                                        {/* Only show current password field if user already has a password */}
                                        {authStatus?.has_password && (
                                            <div className="space-y-2">
                                                <Label htmlFor="currentPassword">Current Password</Label>
                                                <Input
                                                    id="currentPassword"
                                                    type="password"
                                                    value={currentPassword}
                                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                                    placeholder="Enter current password"
                                                    required
                                                    data-testid="current-password-input"
                                                    autoComplete="current-password"
                                                    aria-required="true"
                                                />
                                            </div>
                                        )}
                                        <div className="space-y-2">
                                            <Label htmlFor="newPassword">
                                                {authStatus?.has_password ? 'New Password' : 'Password'}
                                            </Label>
                                            <Input
                                                id="newPassword"
                                                type="password"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                placeholder="Enter password (min 6 characters)"
                                                required
                                                minLength={6}
                                                data-testid="new-password-input"
                                                autoComplete="new-password"
                                                aria-required="true"
                                                aria-describedby="password-hint"
                                            />
                                            <p id="password-hint" className="sr-only">
                                                Password must be at least 6 characters long
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="confirmPassword">Confirm Password</Label>
                                            <Input
                                                id="confirmPassword"
                                                type="password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                placeholder="Confirm password"
                                                required
                                                data-testid="confirm-password-input"
                                                autoComplete="new-password"
                                                aria-required="true"
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <Button 
                                                type="submit"
                                                disabled={savingPassword}
                                                className="bg-accent text-accent-foreground hover:bg-accent/90"
                                                data-testid="save-password-btn"
                                                aria-busy={savingPassword}
                                            >
                                                {savingPassword ? (
                                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                ) : (
                                                    <Save className="h-4 w-4 mr-2" />
                                                )}
                                                {authStatus?.has_password ? 'Change Password' : 'Create Password'}
                                            </Button>
                                            <Button 
                                                type="button"
                                                variant="outline"
                                                onClick={() => {
                                                    setShowPasswordForm(false);
                                                    setCurrentPassword('');
                                                    setNewPassword('');
                                                    setConfirmPassword('');
                                                }}
                                                disabled={savingPassword}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="space-y-4">
                                        {/* Show current auth methods */}
                                        {authStatus?.auth_methods && authStatus.auth_methods.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mb-4">
                                                <span className="text-sm text-muted-foreground">Active login methods:</span>
                                                {authStatus.auth_methods
                                                    .filter(method => method !== 'password') // Filter out password - shown separately
                                                    .map(method => (
                                                        <Badge key={method} variant="secondary" className="capitalize">
                                                            <ShieldCheck className="h-3 w-3 mr-1" />
                                                            {method === 'magic_link' ? 'Magic Link' : method}
                                                        </Badge>
                                                    ))
                                                }
                                                {authStatus.has_password && (
                                                    <Badge variant="secondary">
                                                        <Lock className="h-3 w-3 mr-1" />
                                                        Password
                                                    </Badge>
                                                )}
                                            </div>
                                        )}
                                        
                                        {authStatus?.has_password ? (
                                            <Button 
                                                variant="outline"
                                                onClick={() => setShowPasswordForm(true)}
                                                data-testid="change-password-btn"
                                            >
                                                <Lock className="h-4 w-4 mr-2" />
                                                Change Password
                                            </Button>
                                        ) : (
                                            <div className="space-y-3">
                                                <p className="text-sm text-muted-foreground">
                                                    Add a password to your account so you can sign in with email and password 
                                                    in addition to your current login method.
                                                </p>
                                                <Button 
                                                    variant="outline"
                                                    onClick={() => setShowPasswordForm(true)}
                                                    className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                                                    data-testid="set-password-btn"
                                                >
                                                    <Plus className="h-4 w-4 mr-2" />
                                                    Add Password Login
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Security Section - Logout from all devices */}
                        <Card data-testid="security-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <LogOut className="h-5 w-5" />
                                    Security
                                </CardTitle>
                                <CardDescription>
                                    Manage your active sessions
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <p className="text-sm text-muted-foreground">
                                        If you suspect unauthorized access to your account, you can log out from all devices at once.
                                    </p>
                                    <Button 
                                        variant="outline"
                                        className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                                        onClick={async () => {
                                            setLoggingOutAll(true);
                                            const result = await logoutAllDevices();
                                            setLoggingOutAll(false);
                                            if (result.success) {
                                                toast.success('Logged out from all devices');
                                            } else {
                                                toast.error(result.error || 'Failed to logout');
                                            }
                                        }}
                                        disabled={loggingOutAll}
                                        data-testid="logout-all-devices-btn"
                                    >
                                        {loggingOutAll ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                            <LogOut className="h-4 w-4 mr-2" />
                                        )}
                                        Logout from All Devices
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Purchase History */}
                        <Card data-testid="purchase-history-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <ShoppingBag className="h-5 w-5" />
                                    Purchase History
                                </CardTitle>
                                <CardDescription>
                                    Your recent transactions
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="h-6 w-6 animate-spin text-accent" />
                                    </div>
                                ) : purchases.length === 0 ? (
                                    <div className="text-center py-8">
                                        <ShoppingBag className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                                        <p className="text-muted-foreground">No purchases yet</p>
                                        <Button asChild variant="link" className="text-accent mt-2">
                                            <Link to="/courses">Browse Courses</Link>
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {purchases.map((purchase) => (
                                            <div 
                                                key={purchase.id || purchase.session_id}
                                                className="flex items-center justify-between p-3 rounded-lg border bg-secondary/30"
                                                data-testid={`purchase-${purchase.id || purchase.session_id}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded flex items-center justify-center ${
                                                        purchase.payment_status === 'paid' 
                                                            ? 'bg-green-100 text-green-600' 
                                                            : 'bg-yellow-100 text-yellow-600'
                                                    }`}>
                                                        {purchase.payment_status === 'paid' ? (
                                                            <CheckCircle className="h-5 w-5" />
                                                        ) : (
                                                            <Clock className="h-5 w-5" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-sm">
                                                            {purchase.metadata?.video_title || `Video #${purchase.video_id}`}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {formatDate(purchase.created_at)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-semibold">{formatPrice(purchase.amount)}</p>
                                                    <Badge 
                                                        variant={purchase.payment_status === 'paid' ? 'default' : 'secondary'}
                                                        className={purchase.payment_status === 'paid' ? 'bg-green-100 text-green-700' : ''}
                                                    >
                                                        {purchase.payment_status === 'paid' ? 'Completed' : 'Pending'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Quick Stats */}
                        <Card data-testid="profile-stats">
                            <CardHeader>
                                <CardTitle className="text-lg">Your Library</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Video className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-sm">Videos Owned</span>
                                    </div>
                                    <span className="font-semibold">{totalVideosOwned}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-sm">Purchases</span>
                                    </div>
                                    <span className="font-semibold">{purchases.filter(p => p.payment_status === 'paid').length}</span>
                                </div>
                                <Separator />
                                <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90">
                                    <Link to="/my-library">
                                        View My Library
                                    </Link>
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Admin Badge */}
                        {user?.is_admin && (
                            <Card className="border-purple-200 bg-purple-50">
                                <CardContent className="pt-6">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Badge className="bg-purple-600">Admin</Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-3">
                                        You have administrator access
                                    </p>
                                    <Button asChild variant="outline" size="sm" className="w-full">
                                        <Link to="/admin">Go to Admin Dashboard</Link>
                                    </Button>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
